/**
 * Centralized localStorage manager
 * Ensures localStorage keys are only cleared when starting a new game, not during scrolling or other operations
 */

/**
 * All game-related localStorage keys
 * Add new keys here to ensure they're properly managed
 */
const GAME_STORAGE_KEYS = [
  'rychle-sipy-a-velikonocni-poklad:progress', // Main save state (includes inventory, choices, minigames, etc.)
] as const

export type GameStorageKey = (typeof GAME_STORAGE_KEYS)[number]

/**
 * Centralized localStorage manager
 * Provides safe access to localStorage that prevents accidental clearing during scroll
 */
export class LocalStorageManager {
  /**
   * Get all game-related localStorage keys
   */
  static getGameKeys(): readonly string[] {
    return GAME_STORAGE_KEYS
  }

  /**
   * Check if a key is a game-related key
   */
  static isGameKey(key: string): boolean {
    return GAME_STORAGE_KEYS.includes(key as GameStorageKey)
  }

  /**
   * Clear all game-related localStorage keys
   * This should ONLY be called when starting a new game
   */
  static clearGameData(): void {
    try {
      GAME_STORAGE_KEYS.forEach((key) => {
        localStorage.removeItem(key)
      })
    } catch (error) {
      console.warn('Failed to clear game data from localStorage:', error)
    }
  }

  /**
   * Clear a specific game key
   */
  static clearKey(key: GameStorageKey): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.warn(`Failed to clear localStorage key "${key}":`, error)
    }
  }

  /**
   * Get value from localStorage
   */
  static getItem(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch (error) {
      console.warn(`Failed to get localStorage key "${key}":`, error)
      return null
    }
  }

  /**
   * Set value in localStorage
   */
  static setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value)
    } catch (error) {
      console.warn(`Failed to set localStorage key "${key}":`, error)
    }
  }

  /**
   * Remove a specific item from localStorage
   * Only allows removing game keys to prevent accidental deletion
   */
  static removeItem(key: GameStorageKey): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.warn(`Failed to remove localStorage key "${key}":`, error)
    }
  }
}
